import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, MapPin, Clock, Star, ChevronRight, Phone, Mail, Calendar, Users, Award } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandyCeremonyPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedPackage, setSelectedPackage] = useState('vip');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    partySize: 1,
    packageType: 'vip',
    customDetails: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/booking`, {
        ...formData,
        tourType: `brandy-ceremony-${formData.packageType}`,
        addons: formData.packageType === 'vip-plus-tour' ? ['hollywood-tour'] : []
      });
      toast({
        title: "VIP Reservation Submitted!",
        description: "Your Brandy Star Ceremony VIP reservation has been received. We will confirm your spot shortly.",
      });
      setFormData({ name: '', email: '', phone: '', partySize: 1, packageType: 'vip', customDetails: '' });
    } catch {
      toast({
        title: "Reservation Saved",
        description: "Your reservation has been saved. We'll contact you shortly to confirm!",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const vipIncludes = [
    { text: 'Preferred Viewing Positioning', detail: 'Secured through our IATA-accredited relationships' },
    { text: 'Escorted VIP Arrival', detail: 'From the W Hollywood Hotel to the ceremony site' },
    { text: 'Limited-Edition Commemorative Program', detail: 'Biography, Walk of Fame map, and keepsake' },
    { text: 'Insider Narration', detail: 'From the original IATA-accredited Hollywood tour operator' },
  ];

  const timeline = [
    { time: '10:00 AM', action: 'VIP setup at W Hollywood Hotel entrance' },
    { time: '10:15 AM', action: 'VIP guest check-in. Receive commemorative program.' },
    { time: '10:15 - 10:45 AM', action: 'Gather, mingle, and hear insider context about Brandy\'s legacy' },
    { time: '10:45 AM SHARP', action: 'Escorted group walk to 6201 Hollywood Blvd' },
    { time: '11:30 AM', action: 'Ceremony begins. Guests in preferred viewing area.' },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden" data-testid="brandy-hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/4700108/pexels-photo-4700108.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Hollywood Walk of Fame"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-gray-950" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
        </div>

        {/* Gold particles */}
        <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="gold-particle"
              style={{
                left: `${5 + (i * 7.5) % 90}%`,
                animationDuration: `${6 + (i % 4) * 2}s`,
                animationDelay: `${(i * 0.8) % 4}s`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          {/* Back nav */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="absolute top-8 left-4 md:left-0"
          >
            <button
              onClick={() => navigate('/')}
              className="text-white/50 hover:text-yellow-500 text-sm tracking-wider uppercase flex items-center gap-2 transition-colors"
              data-testid="back-to-home-btn"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
              Hollywood Fame Tour
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}>
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08] backdrop-blur-xl text-xs tracking-[0.25em] uppercase mb-8" style={{ color: '#e8d48b' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
              Official VIP Experience — Limited Availability
            </span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5 }}>
            <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Official Hollywood Tours LLC | IATA Accredited
            </p>
            <h1 className="mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="brandy-page-title">
              <span className="block text-4xl sm:text-5xl lg:text-6xl font-black leading-tight text-white">
                The 2,839th Star
              </span>
              <span className="block text-5xl sm:text-6xl lg:text-7xl font-black leading-tight gold-gradient-text mt-2">
                BRANDY
              </span>
            </h1>
          </motion.div>

          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 0.9 }} className="flex items-center justify-center gap-4 my-6">
            <div className="h-px w-16 md:w-24 bg-gradient-to-r from-transparent to-yellow-500/60" />
            <Star className="h-5 w-5 text-yellow-500/60 fill-yellow-500/30" />
            <div className="h-px w-16 md:w-24 bg-gradient-to-l from-transparent to-yellow-500/60" />
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }}>
            <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto mb-4 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              March 30, 2026 — Hollywood Walk of Fame
            </p>
            <p className="text-sm md:text-base text-white/45 max-w-2xl mx-auto mb-10 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Witness history as Grammy-winning artist Brandy receives her star on the Hollywood Walk of Fame — one of Hollywood's most iconic attractions.
              <br />
              <span className="text-yellow-500/70">The only VIP Hollywood sightseeing tour package from the original IATA-accredited Hollywood tour operator.</span>
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.4 }} className="flex flex-wrap gap-4 justify-center">
            <Button
              size="lg"
              data-testid="brandy-reserve-btn"
              className="bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 hover:from-yellow-500 hover:via-yellow-400 hover:to-yellow-500 text-black px-10 py-7 text-base font-black tracking-wide shadow-[0_0_40px_rgba(212,175,55,0.35)] hover:shadow-[0_0_60px_rgba(212,175,55,0.55)] transition-all duration-500 border-0 rounded-full"
              onClick={() => document.getElementById('brandy-booking').scrollIntoView({ behavior: 'smooth' })}
            >
              SECURE YOUR VIP PLACE
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>

          {/* Event meta cards */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.7 }} className="flex flex-wrap gap-3 md:gap-5 justify-center mt-12">
            {[
              { icon: Calendar, label: 'March 30, 2026' },
              { icon: Clock, label: '10:15 AM Check-in' },
              { icon: MapPin, label: 'W Hollywood Hotel' },
              { icon: Award, label: 'IATA Accredited' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] backdrop-blur-xl rounded-full border border-white/[0.08] text-xs text-white/50">
                <item.icon className="h-3.5 w-3.5 text-yellow-500/70" />
                <span>{item.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Brandy Biography Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950" data-testid="brandy-biography">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-yellow-500/70 font-semibold mb-4">About the Honoree</p>
            <h2 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="gold-gradient-text">Brandy Norwood</span>
            </h2>
            <p className="text-sm text-white/40 tracking-wider uppercase">Singer | Songwriter | Actress</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-10 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <div className="space-y-4 text-white/60 leading-relaxed text-sm md:text-base" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <p>
                  Grammy Award-winning artist. Star of UPN's <em className="text-white/80">"Moesha."</em> Cinderella in Rodgers & Hammerstein's iconic 1997 television film. Multi-platinum recording artist with timeless hits including <em className="text-white/80">"The Boy Is Mine,"</em> <em className="text-white/80">"Sittin' Up in My Room,"</em> and <em className="text-white/80">"Have You Ever?"</em>
                </p>
                <p>
                  A multi-hyphenate talent who shaped R&B and television for a generation — from her breakout as a teenage recording prodigy to Broadway's <em className="text-white/80">"Chicago"</em> and beyond.
                </p>
              </div>

              <div className="mt-8 space-y-3">
                <h3 className="text-sm font-bold text-yellow-500/80 tracking-wider uppercase mb-4">Career Highlights</h3>
                {[
                  'Grammy Award — Best R&B Performance by a Duo or Group (1999)',
                  '11 additional Grammy nominations',
                  'Television: "Moesha," "The Game," "Star," "Queens"',
                  'Broadway: "Chicago"',
                  'Film: "I Still Know What You Did Last Summer"',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Star className="h-3.5 w-3.5 text-yellow-500/50 mt-1.5 flex-shrink-0 fill-yellow-500/30" />
                    <span className="text-white/50 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              {/* Star location card */}
              <Card className="border border-yellow-500/20 bg-white/[0.03] backdrop-blur-sm overflow-hidden">
                <div className="bg-gradient-to-r from-yellow-600/20 to-yellow-500/10 border-b border-yellow-500/20 px-6 py-4">
                  <h3 className="text-sm font-bold text-yellow-500 tracking-wider uppercase flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Star Location Guide
                  </h3>
                </div>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <p className="text-lg font-black text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Brandy's Star</p>
                    <p className="text-white/50 text-sm">6201 Hollywood Boulevard</p>
                    <p className="text-white/35 text-xs mt-1">Between Hollywood & Argyle</p>
                  </div>

                  <div className="border-t border-white/10 pt-4">
                    <p className="text-xs font-bold text-yellow-500/60 tracking-wider uppercase mb-3">Notable Stars Nearby</p>
                    <div className="space-y-2">
                      {[
                        { address: '6201', name: 'Brandy', year: '2026', highlight: true },
                        { address: '6201', name: 'Rihanna', year: '2015' },
                        { address: '6201', name: 'Pharrell Williams', year: '2014' },
                        { address: 'Hollywood & Highland', name: 'Marilyn Monroe, Frank Sinatra, Michael Jackson', year: '' },
                      ].map((star, i) => (
                        <div key={i} className={`flex items-start gap-3 text-sm ${star.highlight ? 'text-yellow-500' : 'text-white/40'}`}>
                          <Star className={`h-3 w-3 mt-1 flex-shrink-0 ${star.highlight ? 'fill-yellow-500 text-yellow-500' : 'text-white/20'}`} />
                          <span>
                            <span className="text-white/20 text-xs">{star.address}:</span>{' '}
                            <span className={star.highlight ? 'font-bold' : ''}>{star.name}</span>
                            {star.year && <span className="text-white/20 text-xs ml-1">({star.year})</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-4">
                    <p className="text-xs font-bold text-yellow-500/60 tracking-wider uppercase mb-2">VIP Meeting Point</p>
                    <p className="text-white/60 text-sm">W Hollywood Hotel</p>
                    <p className="text-white/35 text-xs">Entrance at 6250 Hollywood Blvd</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VIP Package Includes */}
      <section className="py-20 px-4 bg-gray-950 border-t border-white/[0.05]" data-testid="brandy-vip-includes">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-yellow-500/70 font-semibold mb-4">What's Included</p>
            <h2 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your VIP <span className="gold-gradient-text">Experience</span>
            </h2>
            <p className="text-sm md:text-base text-white/40 max-w-2xl mx-auto">
              Everything you need for a truly exclusive Walk of Fame ceremony experience
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {vipIncludes.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
              >
                <Card className="border border-yellow-500/15 bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-300 h-full">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-600/20 to-yellow-500/10 flex items-center justify-center flex-shrink-0 border border-yellow-500/20">
                      <Check className="h-5 w-5 text-yellow-500" />
                    </div>
                    <div>
                      <p className="font-bold text-white text-sm mb-1">{item.text}</p>
                      <p className="text-white/40 text-xs leading-relaxed">{item.detail}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Day-of Timeline */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 border-t border-white/[0.05]" data-testid="brandy-timeline">
        <div className="container mx-auto max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-yellow-500/70 font-semibold mb-4">Day-of Schedule</p>
            <h2 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ceremony <span className="gold-gradient-text">Timeline</span>
            </h2>
            <p className="text-sm text-white/40">March 30, 2026 — W Hollywood Hotel to 6201 Hollywood Blvd</p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-yellow-500/40 via-yellow-500/20 to-transparent" />

            <div className="space-y-8">
              {timeline.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="flex items-start gap-5 ml-0"
                >
                  <div className="relative z-10 flex-shrink-0">
                    <div className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-gradient-to-br from-yellow-600/20 to-yellow-500/10 flex items-center justify-center border border-yellow-500/30">
                      <Clock className="h-4 w-4 md:h-5 md:w-5 text-yellow-500/70" />
                    </div>
                  </div>
                  <div className="pt-2 md:pt-4">
                    <Badge className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 text-xs mb-2">{item.time}</Badge>
                    <p className="text-white/70 text-sm leading-relaxed">{item.action}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-gray-950 border-t border-white/[0.05]" data-testid="brandy-pricing">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-yellow-500/70 font-semibold mb-4">Secure Your Place</p>
            <h2 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              VIP <span className="gold-gradient-text">Packages</span>
            </h2>
            <p className="text-sm text-white/40 max-w-xl mx-auto">Reservations required. Space is strictly limited.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* VIP Package */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <Card
                className={`border overflow-hidden cursor-pointer transition-all duration-300 h-full ${
                  selectedPackage === 'vip'
                    ? 'border-yellow-500/50 shadow-[0_0_30px_rgba(212,175,55,0.15)] bg-white/[0.04]'
                    : 'border-white/10 bg-white/[0.02] hover:border-yellow-500/30'
                }`}
                onClick={() => {
                  setSelectedPackage('vip');
                  setFormData(prev => ({ ...prev, packageType: 'vip' }));
                }}
                data-testid="vip-package-card"
              >
                <div className="bg-gradient-to-r from-yellow-600/15 to-yellow-500/5 border-b border-yellow-500/20 px-6 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-black text-white" style={{ fontFamily: "'Playfair Display', serif" }}>VIP Package</h3>
                    <p className="text-white/40 text-xs">Ceremony Experience</p>
                  </div>
                  <Badge className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 text-sm font-black">$150</Badge>
                </div>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {['Preferred viewing positioning', 'Escorted arrival from W Hollywood Hotel', 'Limited-edition commemorative program', 'Insider narration from IATA-accredited operator'].map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-white/50">
                        <Check className="h-4 w-4 text-yellow-500/70 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* VIP + Tour Package */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}>
              <Card
                className={`border overflow-hidden cursor-pointer transition-all duration-300 h-full relative ${
                  selectedPackage === 'vip-plus-tour'
                    ? 'border-yellow-500/50 shadow-[0_0_30px_rgba(212,175,55,0.15)] bg-white/[0.04]'
                    : 'border-white/10 bg-white/[0.02] hover:border-yellow-500/30'
                }`}
                onClick={() => {
                  setSelectedPackage('vip-plus-tour');
                  setFormData(prev => ({ ...prev, packageType: 'vip-plus-tour' }));
                }}
                data-testid="vip-plus-tour-card"
              >
                <div className="absolute top-0 right-0">
                  <Badge className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black rounded-none rounded-bl-lg px-3 py-1 text-xs font-black">BEST VALUE</Badge>
                </div>
                <div className="bg-gradient-to-r from-yellow-600/15 to-yellow-500/5 border-b border-yellow-500/20 px-6 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-black text-white" style={{ fontFamily: "'Playfair Display', serif" }}>VIP + Full Tour</h3>
                    <p className="text-white/40 text-xs">Ceremony + Hollywood Fame Tour</p>
                  </div>
                  <Badge className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 text-sm font-black">$365</Badge>
                </div>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {[
                      'Everything in VIP Package',
                      'Full Hollywood Fame Tour after ceremony',
                      'Gourmet breakfast & lunch included',
                      'Beverly Hills, Rodeo Drive, Hollywood Sign & more',
                      'Hotel pickup & drop-off',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-white/50">
                        <Check className="h-4 w-4 text-yellow-500/70 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="brandy-booking" className="py-20 px-4 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 border-t border-white/[0.05]" data-testid="brandy-booking-section">
        <div className="container mx-auto max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-yellow-500/70 font-semibold mb-4">Reserve Now</p>
            <h2 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Secure Your <span className="gold-gradient-text">VIP Place</span>
            </h2>
            <p className="text-sm text-white/40 max-w-xl mx-auto">Reservations are required. Space is strictly limited.</p>
          </motion.div>

          <Card className="border border-yellow-500/20 bg-white/[0.03] backdrop-blur-sm shadow-[0_0_40px_rgba(212,175,55,0.05)]">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="brandy-booking-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-white/50 mb-2 tracking-wider uppercase">Full Name *</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your full name"
                      className="bg-white/[0.05] border-white/10 text-white placeholder:text-white/20 focus:border-yellow-500/50"
                      data-testid="brandy-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-white/50 mb-2 tracking-wider uppercase">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your@email.com"
                      className="bg-white/[0.05] border-white/10 text-white placeholder:text-white/20 focus:border-yellow-500/50"
                      data-testid="brandy-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-white/50 mb-2 tracking-wider uppercase">Phone *</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+1 (555) 123-4567"
                      className="bg-white/[0.05] border-white/10 text-white placeholder:text-white/20 focus:border-yellow-500/50"
                      data-testid="brandy-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-white/50 mb-2 tracking-wider uppercase">Number of Guests *</label>
                    <Input
                      type="number"
                      name="partySize"
                      min="1"
                      value={formData.partySize}
                      onChange={handleInputChange}
                      required
                      className="bg-white/[0.05] border-white/10 text-white placeholder:text-white/20 focus:border-yellow-500/50"
                      data-testid="brandy-guests-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-white/50 mb-2 tracking-wider uppercase">Selected Package</label>
                  <select
                    name="packageType"
                    value={formData.packageType}
                    onChange={(e) => {
                      handleInputChange(e);
                      setSelectedPackage(e.target.value);
                    }}
                    className="w-full p-3 bg-white/[0.05] border border-white/10 text-white rounded-md focus:border-yellow-500/50 focus:outline-none"
                    data-testid="brandy-package-select"
                  >
                    <option value="vip" className="bg-gray-900">VIP Package — $150/person</option>
                    <option value="vip-plus-tour" className="bg-gray-900">VIP + Full Hollywood Tour — $365/person</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-white/50 mb-2 tracking-wider uppercase">Special Requests (Optional)</label>
                  <Textarea
                    name="customDetails"
                    value={formData.customDetails}
                    onChange={handleInputChange}
                    placeholder="Any special requirements or questions..."
                    rows={3}
                    className="bg-white/[0.05] border-white/10 text-white placeholder:text-white/20 focus:border-yellow-500/50"
                    data-testid="brandy-details-textarea"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="brandy-submit-btn"
                  className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-black font-black py-7 text-lg shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:shadow-[0_0_50px_rgba(212,175,55,0.5)] hover:scale-[1.02] transition-all disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'SUBMIT VIP RESERVATION'}
                </Button>

                <p className="text-center text-xs text-white/30">
                  Payment details will be collected after we confirm availability. Space is limited.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Commemorative Program Preview */}
      <section className="py-20 px-4 bg-gray-950 border-t border-white/[0.05]" data-testid="brandy-commemorative">
        <div className="container mx-auto max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-yellow-500/70 font-semibold mb-4">Included With Every VIP Package</p>
            <h2 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Commemorative <span className="gold-gradient-text">Keepsake</span>
            </h2>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <Card className="border border-yellow-500/20 bg-gradient-to-br from-white/[0.04] to-white/[0.01] overflow-hidden">
              <CardContent className="p-8 md:p-10 text-center">
                <div className="inline-block border border-yellow-500/20 rounded-xl p-8 md:p-10 bg-black/30 backdrop-blur-sm max-w-md mx-auto">
                  <p className="text-[10px] tracking-[0.4em] uppercase text-white/30 mb-6">Official Hollywood Tours LLC — IATA Accredited</p>
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="h-px w-12 bg-yellow-500/30" />
                    <Star className="h-4 w-4 text-yellow-500/50 fill-yellow-500/30" />
                    <div className="h-px w-12 bg-yellow-500/30" />
                  </div>
                  <p className="text-white/40 text-xs tracking-wider uppercase mb-2">The 2,839th Star</p>
                  <h3 className="text-3xl md:text-4xl font-black gold-gradient-text mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    BRANDY
                  </h3>
                  <p className="text-white/30 text-xs tracking-wider">March 30, 2026</p>
                  <div className="flex items-center justify-center gap-3 mt-6">
                    <div className="h-px w-12 bg-yellow-500/30" />
                    <Star className="h-4 w-4 text-yellow-500/50 fill-yellow-500/30" />
                    <div className="h-px w-12 bg-yellow-500/30" />
                  </div>
                  <p className="text-[10px] text-white/20 mt-6 italic">VIP Commemorative Program</p>
                </div>
                <p className="text-white/30 text-xs mt-6 leading-relaxed max-w-sm mx-auto">
                  Each VIP guest receives a professionally printed tri-fold commemorative program featuring Brandy's career biography, Walk of Fame star map, and exclusive keepsake content.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 border-t border-white/[0.05]">
        <div className="container mx-auto max-w-5xl text-center">
          <p className="text-lg font-black mb-2 gold-gradient-text" style={{ fontFamily: "'Playfair Display', serif" }}>Hollywood Fame Tour</p>
          <p className="text-white/30 text-xs tracking-wider uppercase mb-6">The Original IATA-Accredited Hollywood Tour Operator — Est. 2010</p>
          <div className="flex flex-wrap justify-center gap-6 mb-6 text-sm text-white/40">
            <a href="tel:+18777418390" className="flex items-center gap-2 hover:text-yellow-500 transition-colors">
              <Phone className="h-3.5 w-3.5" />
              (877) 741-8390
            </a>
            <a href="mailto:info@officialhollywoodtours.com" className="flex items-center gap-2 hover:text-yellow-500 transition-colors">
              <Mail className="h-3.5 w-3.5" />
              info@officialhollywoodtours.com
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-4 text-xs text-white/25">
            <button onClick={() => navigate('/')} className="hover:text-yellow-500 transition-colors">Home</button>
            <span>|</span>
            <a href="/#tours" className="hover:text-yellow-500 transition-colors">Tours</a>
            <span>|</span>
            <button onClick={() => navigate('/concierge')} className="hover:text-yellow-500 transition-colors">Hotel Concierge</button>
            <span>|</span>
            <a href="https://officialhollywoodtours.com" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-500 transition-colors">officialhollywoodtours.com</a>
          </div>
          <p className="text-white/15 text-xs mt-8">&copy; 2026 Official Hollywood Tours LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BrandyCeremonyPage;
